import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { CONTROL, CTA } from "@/lib/typography";
import { cn } from "@/lib/utils";

const filledDisabled = "disabled:bg-(--control-fill) disabled:text-muted-foreground disabled:border-(--control-line) disabled:opacity-100 disabled:bg-none";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-[1.5px] font-semi-bold text-h6 ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0 data-[active=true]:bg-foreground data-[active=true]:text-background data-[active=true]:border-foreground",
  {
    variants: {
      variant: {
        default:
          ["bg-brand-accent text-brand-on-accent border-brand-accent", filledDisabled],
        // Gradient button - orange gradient used for primary CTAs in modals and profile
        gradient:
          ["bg-brand-gradient text-white border-0", filledDisabled],
        // Add-to-stasht pill - used for "+ Add" overlays and header actions
        // Uses an inset ring so the "border" doesn't affect pill size/layout.
        "add-to-stasht":
          ["bg-brand-gradient text-white border-0 ring-1 ring-inset ring-white/30 dark:ring-white/25", filledDisabled],
        destructive: ["bg-destructive text-destructive-foreground border-destructive", filledDisabled],
        outline:
          ["border border-input bg-background text-foreground", filledDisabled],
        // Filled on Canvas, Canvas with a hairline inside a shared Surface.
        secondary:
          ["bg-(--control-fill) text-foreground border-(--control-line)", filledDisabled],
        // Quiet view configuration and utility actions; keep callers free of color overrides.
        ghost:
          "text-muted-foreground bg-transparent border-transparent hover:bg-muted hover:text-foreground",
        link: "text-brand-accent underline-offset-4 border-transparent hover:underline disabled:text-muted-foreground disabled:opacity-100",
      },
      size: {
        // Default size - Phase 3: 24px horizontal (px-6), 12px vertical (py-3)
        default: "h-auto px-6 py-3",
        sm: `h-auto px-4 py-2 ${CONTROL} rounded-full border-[1.5px]`,
        xs: "h-auto px-3 py-1.5 text-ui-caption rounded-full border-[1.5px] font-semi-bold", // Extra small for category pills and compact buttons
        lg: `h-auto px-8 py-4 ${CTA} rounded-full border-[1.5px]`,
        icon: "h-10 w-10 p-0 rounded-full border-[1.5px]",
        // Full-height actions beside inventory search fields.
        control: `h-(--control-height) px-[15px] py-2.5 rounded-(--radius-control) ${CONTROL} leading-control [&_svg]:size-[16px]`,
        "icon-control": "relative size-(--control-height) shrink-0 p-0 rounded-(--radius-control) [&_svg]:size-[16px]",
        // Modal size - used for full-width buttons in modals with rounded-xl corners
        modal: `h-auto w-full py-3 rounded-xl ${CTA}`,
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  active?: boolean; // For category pills and active states
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    // Extract active prop if provided (for category pills)
    const { active, ...restProps } = props as ButtonProps & { active?: boolean };
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        data-active={active}
        {...restProps}
      />
    );
  },
);
Button.displayName = "Button";

// Keep counts inside the control's bounds, including at a scroll container edge.
// The button's accessible name supplies the count to assistive technology.
function ButtonCountBadge({ children }: { children: React.ReactNode }) {
  return (
    <span aria-hidden="true" className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-0.5 text-ui-badge font-medium leading-none text-background">
      {children}
    </span>
  );
}

export { Button, ButtonCountBadge, buttonVariants };
